import { ChangeEvent } from "react";

import { Checkbox } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { capitalize } from "@s-libs/micro-dash";

import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { FilterSection } from "@/search/NewSearchPageMVP/shared/FilterSection";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { readingSkillCheckboxCss } from "./ReadingSkills.styles";

const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const getEventProperties = (skill: string) => ({
  objectType: objectTypes.content,
  actionPrefix: actionPrefixes.initiate,
  actionName: actionNames.filter,
  streamProperties: {
    description: `Filter search by the ${skill} reading skill`,
  },
});

export const ReadingSkills = () => {
  const {
    getters: { readingSkills = [] },
    handlers: {
      hasSearchParamValue,
      toggleSearchParamValue,
      getSearchParamValues,
    },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  if (!readingSkills.length) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSearchParamValue(
      AgathaSchemaParamsEnum.reading_skills,
      e.target.value,
    );
  };

  const hasNoReadingSkillsValues =
    (getSearchParamValues(AgathaSchemaParamsEnum.reading_skills) || [])
      .length === 0;

  return (
    <>
      {readingSkills && (
        <FilterSection
          title="Reading skills"
          defaultCollapsed={hasNoReadingSkillsValues}
        >
          {readingSkills.map((readingSkill) => (
            <CheckboxWithTrackEvent
              onChange={handleChange}
              key={readingSkill.slug}
              label={capitalize(readingSkill.text)}
              value={readingSkill.slug}
              checked={hasSearchParamValue(
                AgathaSchemaParamsEnum.reading_skills,
                readingSkill.slug,
              )}
              __cssFor={readingSkillCheckboxCss}
              dataProps={{
                "data-qa-selector": `select-${readingSkill.text.replace(" ", "-")}-search-filter`,
              }}
              {...getEventProperties(readingSkill.text)}
            />
          ))}
        </FilterSection>
      )}
    </>
  );
};
